import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import { api } from '../api';
import { condition, fmtTemp, isExtended, modelName, type WeatherPrefs, type WeatherView } from '../weather';
import HourlyStrip from './HourlyStrip';
import Popover, { type AnchorRect } from './Popover';
import WeatherDetails from './WeatherDetails';
import WeatherIcon from './WeatherIcon';

interface Props {
  date: string; // YYYY-MM-DD
  isToday: boolean;
  anchor: AnchorRect;
  weather: WeatherView;
  onClose: () => void;
}

export default function WeatherPopover({ date, isToday, anchor, weather, onClose }: Props) {
  const { prefs, data, hoursByDay } = weather;
  const day = data.days[date] ?? null;
  const current = isToday ? data.current : null;
  const nowHour = Math.floor(Date.now() / 3_600_000) * 3600;
  const hours = (hoursByDay.get(date) ?? []).filter((h) => !isToday || h.t >= nowHour);
  const cond = condition(current?.weather_code ?? day?.weather_code);
  const t = prefs.temperature_unit;

  return (
    <Popover anchor={anchor} onClose={onClose} width={380} fitViewport>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <LocationPicker prefs={prefs} />
          <span className="text-[0.78rem] text-[var(--text-muted)]">
            {isToday ? 'Today' : format(parseISO(date), 'EEE, MMM d')}
          </span>
        </div>

        {day || current ? (
          <div className="flex items-center gap-3">
            {cond && (
              <WeatherIcon
                kind={cond.icon}
                night={current?.is_day === 0}
                size={40}
                className="flex-shrink-0 text-[var(--text-muted)]"
              />
            )}
            <div className="flex flex-col gap-[2px]">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular-nums">
                  {fmtTemp(current?.temperature_2m ?? day?.temperature_2m_max, t)}
                </span>
                {cond && <span className="text-sm text-[var(--text-muted)]">{cond.label}</span>}
              </div>
              {day && day.temperature_2m_max != null && (
                <span className="text-[0.78rem] tabular-nums text-[var(--text-muted)]">
                  High {fmtTemp(day.temperature_2m_max, t)}, low {fmtTemp(day.temperature_2m_min, t)}
                </span>
              )}
              {day && isExtended(date, day, prefs) && (
                <span className="text-[0.72rem] text-[var(--text-faint)]">
                  {modelName(day.model)} forecast, beyond {modelName(prefs.model)}’s range
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="m-0 text-sm text-[var(--text-faint)]">No weather for this day.</p>
        )}

        <WeatherDetails day={day} current={current} air={isToday ? data.air : null} prefs={prefs} />
        <HourlyStrip hours={hours} unit={t} maxColumns={8} />

        <WeatherFooter weather={weather} />
      </div>
    </Popover>
  );
}

export function LocationPicker({ prefs }: { prefs: WeatherPrefs }) {
  const qc = useQueryClient();
  const setActive = useMutation({
    mutationFn: (id: string) =>
      api.patch<WeatherPrefs>('/api/weather/preferences', { active_location_id: id }),
    onSuccess: (next) => {
      qc.setQueryData(['weather-prefs'], next);
      qc.invalidateQueries({ queryKey: ['weather'] });
    }
  });
  const active = prefs.locations.find((l) => l.id === prefs.active_location_id);
  if (prefs.locations.length <= 1) {
    return <span className="truncate text-sm font-medium">{active?.name ?? 'No location'}</span>;
  }
  return (
    <select
      value={prefs.active_location_id ?? ''}
      onChange={(e) => setActive.mutate(e.target.value)}
      disabled={setActive.isPending}
      className="min-w-0 max-w-[220px] truncate rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[3px] text-sm font-medium"
    >
      {prefs.locations.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
        </option>
      ))}
    </select>
  );
}

export function WeatherFooter({ weather }: { weather: WeatherView }) {
  const loc = weather.data.location;
  return (
    <div className="flex flex-col gap-1 border-t border-[var(--border)] pt-2 text-[0.7rem] text-[var(--text-faint)]">
      {loc?.fetch_error && (
        <span className="text-[var(--today-fg)]">Last update failed: {loc.fetch_error}</span>
      )}
      <div className="flex items-center justify-between gap-2">
        <span>
          {loc?.fetched_at
            ? `Updated ${format(new Date(loc.fetched_at), 'h:mm a').toLowerCase()} from ${modelName(weather.data.model)}`
            : 'Waiting for the first update'}
        </span>
        <a
          href="https://open-meteo.com/"
          target="_blank"
          rel="noreferrer"
          className="text-[var(--text-faint)] hover:text-[var(--text-muted)]"
        >
          Weather data by Open-Meteo.com
        </a>
      </div>
    </div>
  );
}
